import {IconType} from "components/Icon"
import {WorkDay} from "./work-schedule"
import {EventTypeQuestionnaireListResponse} from "./event-type"

export type CalendarDetails = {
    calendarId: number
    ownerUserId: number
    name: string
    description: string
    createdAt: string
}

export type CalendarEvent = {
    objectId: number
    groupId: number
    calendarId: number
    type: CalendarEventType
    name: string
    description: string
    ownerUserId: number
    createdAt: string
    startAtUtc: string
    endAtUtc: string
    startAtWall: string
    startAtTimezone: string
    duration: number
    rrule: string
    jsonData: {
        [key in string]: string
    }
    started_at?: string | null
}

export type Event = {
    classNames: string
    color: string
    description: string
    end: string
    id: number
    initialEventIndex: number
    isAppointmentSlot: boolean
    order: number
    ownerUserId: number
    start: string
    endDateTime: any
    startDateTime: any
} & {[key in string]: any}

export type CalendarEventOption = {
    title: string
    icon: IconType
    action?: Function
    iconClassName?: string
    canShow?: (event: any) => void
}

export type CalendarProps = {
    departmentId?: number
    calendarRef: any
    events: Event[]
    isLoading: boolean
    viewOptions?: any[]
    workDays?: WorkDay[]
    holidayDays?: any
    timezone?: string
    showConnectedCalendars?: boolean
    onDateClick: (date: any) => void
    onChangeDatesSet: (date: any) => void
    eventOptions: CalendarEventOption[]
    linkedCalendars?: LinkedCalendarInfo[]
    staticOptions?: StaticOption[]
    updateStaticOptions?: (data: StaticOption[]) => void
    updateLinkedCalendars?: (data: LinkedCalendarInfo[]) => void
    viewMode?: "list" | "calendar"
    setViewMode?: (mode: "list" | "calendar") => void
    onSelectedDayChange?: (date: Date) => void
    selectedDay?: Date
} & {[key in string]: any}

export type CreateEventPayload = {
    calendar_id: number
    name: string
    description?: string
    host_notes?: string
    duration: number
    json_data: {
        eventType?: string
        eventTypeId?: number
        color: string
        allDay?: boolean
        notifications?: number[]
        with_sms?: boolean
        with_email?: boolean
        with_push_notification?: boolean
        with_invite_push_notification?: boolean
        with_invite_email?: boolean
        without_rsvp?: boolean
        eventSignInRequired?: boolean
        eventTrackAttendanceMinutes?: boolean
        attendedChangeToStatus?: any
        notAttendedChangeToStatus?: any
        completionWorkflow?: any
        address?: string
        daysOfCourse?: string
        term_id?: number
        course_id?: number
        schedule_id?: number
        sub_module_id?: number
        location?: any
        resources?: any
        departmentId?: number
        department?: any
        campuses?: any[]
        programs?: any
        shifts?: any
        updateProfileStatusId?: number
    }
    invited_user_ids?: number[]
    invited_profile_ids?: number[]
    owner_user_id: number
    rrule: string
    type: string
    start_at_timezone: string
    start_at_wall: string
    edular_group_id?: number
    host_user_ids?: number[]
}

export enum CalendarRecurringType {
    Never = "NEVER",
    Yearly = "YEARLY",
    Monthly = "MONTHLY",
    Weekly = "WEEKLY",
    Daily = "DAILY"
}

export enum Months {
    January,
    February,
    March,
    April,
    May,
    June,
    July,
    August,
    September,
    October,
    November,
    December
}

export enum Day {
    Monday = 1,
    Tuesday,
    Wednesday,
    Thursday,
    Friday,
    Saturday,
    Sunday
}

export enum CalendarEventType {
    Event = "event",
    CourseSchedule = "course_schedule",
    StartTermDate = "start_term_date",
    EndTermDate = "end_term_date",
    StartMajorDate = "start_major_date",
    EndMajorDate = "end_major_date",
    AppointmentSlot = "appointment_slot",
    WorkSchedule = "work_schedule",
    SchoolClosure = "school_closure"
}

export enum RSVP {
    Yes = "yes",
    Maybe = "maybe",
    No = "no",
    NOT_SELECTED = "not_selected"
}

export interface EventType {
    eventTypeId: number
    code: string
    name: string
    label: string
    updatedAt: string
}

export enum RescheduleSelection {
    Class = "class",
    Calendar = "calendar"
}

export enum RecurringPopupType {
    Delete = "Delete",
    Update = "Update"
}

export enum TargetRecurringEvent {
    OnlyThis = "only_this",
    ThisAndFurther = "this_and_further",
    All = "all"
}

export enum RecurringEnd {
    Never = 0,
    AfterExecution = 1,
    OnDate = 2
}

export enum ExternalCalendarProvider {
    Outlook = "outlook",
    Google = "google"
}

export type EventInvitee = {
    invite_id: number
    object_id: number
    created_at: string
    invited_user_id: number
    will_go: RSVP
    rsvp: boolean
    check_in?: string | null
    is_check_in: boolean
    json_data: {}
}

export type ObjectsCreatedAnalyticsGroupBy = "date" | "week" | "month"

export type ObjectsCreatedAnalyticsRequest = {
    userIds: number[]
    eventTypeIds: number[]
    startsAtUtc: string
    endsAtUtc: string
    timezone: string
    groupBy: ObjectsCreatedAnalyticsGroupBy
    outcomeIds: number[]
    campusIds?: number[]
    programIds?: number[]
    termsIds?: number[]
    startDateRange?: [string, string] | null
}

export type ObjectsCreatedAnalyticsItem = {
    bucketDate: string
    userId: number
    total: number
    totalOutcomes: number
    totalAtStart: number
    totalOutcomesAtStart: number
}

export type ConnectedCalendarDetail = {
    id: number
    account: {name: string; provider: ExternalCalendarProvider}
    requiresReconnect: boolean
}

export type GenerateExternalCalendarOAuthLinkParams = {
    provider: ExternalCalendarProvider
    successRedirect: string
}

export type MyCalendar = {
    calendar_id: number
    name: string
    integration_data: ConnectedCalendarDetail
    description: string
    is_primary: boolean
    owner_user_id: number
    sis_id: string
    type?: "group"
    calendar_group_id?: number
}

export enum CalendarStaticFilter {
    Term = "public_terms",
    ProgramStart = "public_programs_start",
    ProgramEnd = "public_programs_end",
    WorkSchedules = "public_work_schedules",
    Booked = "include_booked",
    CourseSchedules = "include_course_schedules",
    Invites = "include_invites"
}

export type LinkedCalendarInfo = {
    isPrimary: boolean
    id: number
    calendarId: number
    name: string
    provider: ExternalCalendarProvider
    requiresReconnect: boolean
    isShow: boolean
    type?: "group"
}

export type StaticOption = {
    id: CalendarStaticFilter
    name: string
    isShow: boolean
}

export type Meeting = {
    meetingId: number
    name: string
    description: string
    plainDescription: string
    url: string
    createdAt: string
    updatedAt: string
    duration: number
    capacity?: number | null
    startTimeIncrements: number
    allowSameDayBooking?: boolean
    maxCalendarViewDays?: number
    isActive: boolean
    startAtUtc: string
    endAtUtc: string
    startAtTimezone: string
    jsonData: any
    weeklyHours: WeeklyHour[]
}

export type WeeklyHour = {
    weekday: number
    ranges: {startTime: string; endTime: string}[]
}

export type CreateMeetingPayload = {
    name: string
    description: string
    duration: number
    capacity?: number | null
    startTimeIncrements: number
    allowSameDayBooking: boolean
    maxCalendarViewDays?: number | null
    startAtWall: string
    endAtWall: string
    startAtTimezone: string
    weeklyHours: WeeklyHour[]
    jsonData: MeetingJsonData
}

export type EditMeetingPayload = {
    meetingId: number
    name?: string
    description?: string
    duration?: number
    capacity?: number | null
    startTimeIncrements?: number
    allowSameDayBooking?: boolean
    maxCalendarViewDays?: number | null
    startAtWall?: string
    endAtWall?: string
    startAtTimezone?: string
    jsonData?: MeetingJsonData
    isActive?: boolean
    weeklyHours?: WeeklyHour[]
}

export type MeetingJsonData = Partial<{
    edularDepartment: {
        name: string
        departmentId: number
    }
    edularEventType: {
        eventTypeId: number
        color: string
        name: string
    }
    remind: {
        withPushNotification?: boolean
        withEmail?: boolean
        withSms?: boolean
        notifications?: number[]
    }
    newProfileLeadSource: {
        name: string
        leadSourceId: number
    }
    newProfileStatus: {
        name: string
        statusId: number
        state: string
    }
    groupCalendarId: number
    groupCalendarName: string
    calendarGroupId?: number
    staffIds?: number[]
    defaultStaffId?: number
}>

export type GetAvailableHoursPayload = {
    hash: string
    dateStart: string
    dateEnd: string
    timezone: string
    staffId?: number
}

export type AvailableHours = {
    date: string
    slots: {startTime: string}[]
}

export type BookMeetingQuestionnaireAnswerPayload = {
    questionId: string
    value: string | number | string[]
}

export type BookMeetingPayload = {
    hash: string
    startTime: string
    timezone: string
    firstName: string
    lastName: string
    phone: string
    email: string
    fafsa?: string
    campusId?: number
    programId?: number
    startDate?: string
    campusName?: string
    programName?: string
    questionnaireAnswers?: BookMeetingQuestionnaireAnswerPayload[]
    staffId?: number
}

export type PublicMeetingStaff = {
    id?: number
    firstName?: string
    lastName?: string
    photo?: Record<string, string>
}

export type PublicMeetingLink = {
    campuses: {campusId: number; name: string}[]
    programs: {programId: number; name: string; campusIds: number[]}[]
    meeting: Meeting
    eventTypeCampuses: {id: number; name: string; code: string; isActive: number}[]
    meetingOwner: {
        id: number
        profileId: number
        firstName: string
        middleName: string
        lastName: string
        customUserId: string
        fullName: string
        name: string
        photo: Record<string, string>
        timeZone: string
        dateFormat: string
    }
    staffs?: PublicMeetingStaff[]
    terms: {termId: number; campusId: number; programId: number; startDate: string; endDate: string}[]
    questionnaireList: EventTypeQuestionnaireListResponse
}

export type ResendInvitePayload = {object_id: number; all_users: boolean} | {object_id: number; user_ids: number[]}

export type CalendarReminderOption = {
    value: number
    label: string
}

export type CalendarEventTypeSettings = {
    signInRequired?: boolean
    trackAttendanceMinutes?: boolean
    notes?: string
    durationInMinutes?: number
    capacity?: number | null
    department?: unknown
    campuses?: {id: number; name: string; code?: string}[]
    notifications?: number[]
    withInvitePushNotification?: boolean | 0 | 1
    withPushNotification?: boolean | 0 | 1
    withoutRsvp?: boolean | 0 | 1
    withInviteEmail?: boolean | 0 | 1
    withEmail?: boolean | 0 | 1
    withSms?: boolean | 0 | 1
    address?: string | null
    rrule?: string | null
    profileStatusId?: number | null
}

export type UserSettings = {
    userId: number
    defaultCalendarId: number
    selectedCalendarIds: number[] | null
    json_data?: Record<string, unknown>
}
